// Phase 999.1 R8 — Userlog Viewer (Plan 07).
//
// Forensic UI for the userlog audit table. Slotted into AdminLayout (Plan 06)
// at /admin/userlog. The killer query is "show me every row for one
// CorrelationId" — that's the first filter operators reach for after a Loki
// alert, so it's first in the form.
//
// SECURITY: This component is rendered behind AdminGuard (UX-only) AND every
// API call goes to GET /api/db/userlog which is server-side gated by
// [Authorize(Policy = "GlobalAdmin")] (Plan 03). The client guard is hygiene;
// the server is the actual security boundary.
//
// Pagination is intentionally cursorless Prev/Next over offset/limit. The
// provider hard-caps limit at 1000 (Plan 02) and clamps offset >= 0; the
// viewer asks for 100 rows per page and exposes Prev/Next based on whether
// the current response was full (== probably more) or short (== last page).
//
// Phase 999.6 Plan 03:
//  - Readable action labels via ACTION_MAP
//  - Expandable InputData summary cell via summariseInputData
//  - Category filter dropdown (additive — #ulv-action-name is preserved)
//  - Row colouring by category/severity via Bootstrap table-* classes
//  - Site/Shot columns from server-side enrichment (Plan 02)
//  - File-download button on file-* rows (Bearer fetch + URL.createObjectURL)

import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Alert, Table } from 'reactstrap';
import { apiBase } from './apiBase';
import { ACTION_MAP, CATEGORIES, categoryToActionNameLike } from './actionMap';
import { summariseInputData } from './inputDataSummary';

const PAGE_SIZE = 100;

// Plain reactstrap Table — react-data-grid v7 was truncating header labels to
// single letters under our Bootstrap/paper-kit cascade and PAGE_SIZE=100 doesn't
// need virtualization. Keys are camelCase to match the JSON shape ASP.NET Core
// emits (actionName/correlationId/userID), not the C# DTO's PascalCase.
//
// siteName/shotName are added by Phase 999.6 Plan 02 server-side enrichment.
const COLUMNS = [
    { key: 'logTime',          name: 'Time'            },
    { key: 'severity',         name: 'Severity'        },
    { key: 'correlationId',    name: 'CorrelationId'   },
    { key: 'actionName',       name: 'Action'          },
    { key: 'inputType',        name: 'InputType'       },
    { key: 'siteName',         name: 'Site'            },
    { key: 'shotName',         name: 'Shot'            },
    { key: 'locationId',       name: 'LocationId'      },
    { key: 'userID',           name: 'UserID'          },
    { key: 'exceptionSummary', name: 'ExceptionSummary'},
];

// Total header columns: COLUMNS + InputData summary + Download (synthetic cells).
const TOTAL_COLS = COLUMNS.length + 2;

function emptyFilters() {
    return {
        from: '',
        to: '',
        correlationId: '',
        severity: '',
        actionName: '',
        locationId: '',
        userId: '',
        category: '',
    };
}

// `<Input type="datetime-local">` emits `YYYY-MM-DDTHH:mm` with NO timezone marker.
// Browsers render the picker in the operator's local timezone, so without a label
// + suffix the operator can't tell whether the server treats the value as UTC or
// local. We fix the contract here: the form is labelled "(UTC)" and we append `Z`
// before submission so ASP.NET Core's DateTime model binder parses the value as
// DateTimeKind.Utc — same wall-clock the userlog rows are stored in.
function toUtcIso(dtLocalValue) {
    if (!dtLocalValue) return '';
    // datetime-local omits seconds when the input lacks step="1"; pad to a
    // canonical ISO that the server's DateTime parser will accept either way.
    const withSeconds = dtLocalValue.length === 16 ? dtLocalValue + ':00' : dtLocalValue;
    return withSeconds + 'Z';
}

function buildQueryString(filters, offset, limit) {
    const params = new URLSearchParams();
    if (filters.from)          params.set('from',          toUtcIso(filters.from));
    if (filters.to)            params.set('to',            toUtcIso(filters.to));
    if (filters.correlationId) params.set('correlationId', filters.correlationId);
    if (filters.severity)      params.set('severity',      filters.severity);
    if (filters.actionName) {
        params.set('actionName', filters.actionName);
    } else if (filters.category) {
        // Category pre-fills the actionName LIKE param when the operator has not
        // typed an explicit manual token. The manual box always wins (Pitfall 5).
        params.set('actionName', categoryToActionNameLike(filters.category));
    }
    if (filters.locationId)    params.set('locationId',    filters.locationId);
    if (filters.userId)        params.set('userId',        filters.userId);
    params.set('offset', String(offset));
    params.set('limit',  String(limit));
    return params.toString();
}

// Maps a Bootstrap contextual color name to a table row className.
// Returns '' for null (forensic tokens — severity drives the class instead).
function colorToRowClass(color) {
    if (!color) return '';
    return 'table-' + color;
}

// Derives a severity-based row class when the ACTION_MAP entry has color: null.
function severityToRowClass(severity) {
    if (!severity) return '';
    if (severity === 'error')   return 'table-danger';
    if (severity === 'warn')    return 'table-warning';
    return '';
}

// API base resolver moved to ./apiBase.js (Plan 05) so SitesTable / Wizard / Config
// components can share it without copy-paste. Behaviour preserved verbatim.

export function UserLogViewer({ auth }) {
    const [filters,     setFilters]     = useState(emptyFilters());
    const [rows,        setRows]        = useState([]);
    const [offset,      setOffset]      = useState(0);
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState(null);
    const [downloading, setDownloading] = useState(null);   // id of row being downloaded
    const [expandedRow, setExpandedRow] = useState(null);   // id of row with expanded InputData

    const fetchPage = useCallback(async (filtersToUse, offsetToUse) => {
        setLoading(true);
        setError(null);
        try {
            // auth may be missing in unit tests — guard so the component still
            // renders the empty state. The real /admin route always passes it
            // through AdminLayout.
            let accessToken = '';
            if (auth && typeof auth.getAccessToken === 'function') {
                try { accessToken = auth.getAccessToken(); } catch (e) { /* ignore — server will 401 */ }
            }

            const url = apiBase() + '/api/db/userlog?' + buildQueryString(filtersToUse, offsetToUse, PAGE_SIZE);
            const res = await fetch(url, {
                method: 'GET',
                headers: accessToken ? { Authorization: 'Bearer ' + accessToken } : {},
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error('HTTP ' + res.status + (text ? ': ' + text : ''));
            }
            const data = await res.json();
            setRows(Array.isArray(data) ? data : []);
            setOffset(offsetToUse);
        } catch (e) {
            setError(e && e.message ? e.message : String(e));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [auth]);

    // downloadUserLogFile — fetches /api/db/userlog/{id}/file with the Bearer
    // token and triggers a browser download via URL.createObjectURL. Uses fetch
    // (not a plain <a href>) so the Authorization header is always included.
    // Errors (422 pre-encryption, 403 unauthorised) surface in the error Alert.
    const downloadUserLogFile = useCallback(async (id) => {
        setDownloading(id);
        setError(null);
        try {
            let accessToken = '';
            if (auth && typeof auth.getAccessToken === 'function') {
                try { accessToken = auth.getAccessToken(); } catch (e) { /* ignore */ }
            }

            const res = await fetch(apiBase() + '/api/db/userlog/' + id + '/file', {
                method: 'GET',
                headers: accessToken ? { Authorization: 'Bearer ' + accessToken } : {},
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error('HTTP ' + res.status + (text ? ': ' + text : ''));
            }

            const blob = await res.blob();
            const disposition = res.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = match
                ? match[1].replace(/['"]/g, '')
                : 'userlog-' + id + '-file';

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            setError(e && e.message ? e.message : String(e));
        } finally {
            setDownloading(null);
        }
    }, [auth]);

    const onSubmit = useCallback((e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        // Submit always resets to first page — operator just changed the filter,
        // they want fresh results, not "page 7 of the OLD filter".
        fetchPage(filters, 0);
    }, [filters, fetchPage]);

    const onReset = useCallback(() => {
        const cleared = emptyFilters();
        setFilters(cleared);
        fetchPage(cleared, 0);
    }, [fetchPage]);

    const onPrev = useCallback(() => {
        const next = Math.max(0, offset - PAGE_SIZE);
        if (next !== offset) fetchPage(filters, next);
    }, [filters, offset, fetchPage]);

    const onNext = useCallback(() => {
        // Only advance if the current page came back full. A short page means
        // we already have the tail.
        if (rows.length >= PAGE_SIZE) fetchPage(filters, offset + PAGE_SIZE);
    }, [filters, offset, rows.length, fetchPage]);

    const onChange = useCallback((field) => (ev) => {
        const value = ev && ev.target ? ev.target.value : '';
        setFilters((prev) => ({ ...prev, [field]: value }));
    }, []);

    const toggleExpand = useCallback((id) => {
        setExpandedRow((prev) => (prev === id ? null : id));
    }, []);

    const hasPrev = offset > 0;
    const hasNext = rows.length >= PAGE_SIZE;
    const pageStart = rows.length === 0 ? 0 : offset + 1;
    const pageEnd   = offset + rows.length;

    return (
        <Container fluid className="user-log-viewer" data-testid="userlog-viewer-root">
            <h3>Userlog Viewer</h3>
            <p className="text-muted">
                Forensic search across the audit trail. Filter by CorrelationId for
                full-request reconstruction, or by Severity / Action for triage sweeps.
            </p>

            <Form onSubmit={onSubmit} aria-label="userlog-filter-form" data-testid="userlog-filter-form">
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label for="ulv-correlation-id">CorrelationId</Label>
                            <Input
                                id="ulv-correlation-id"
                                name="correlationId"
                                type="text"
                                value={filters.correlationId}
                                onChange={onChange('correlationId')}
                                placeholder="32-char hex TraceId"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label for="ulv-severity">Severity</Label>
                            <Input
                                id="ulv-severity"
                                name="severity"
                                type="select"
                                value={filters.severity}
                                onChange={onChange('severity')}
                            >
                                <option value="">(any)</option>
                                <option value="info">info</option>
                                <option value="warn">warn</option>
                                <option value="error">error</option>
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label for="ulv-category">Category</Label>
                            <Input
                                id="ulv-category"
                                name="category"
                                type="select"
                                value={filters.category}
                                onChange={onChange('category')}
                            >
                                {CATEGORIES.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label for="ulv-action-name">ActionName <small className="text-muted">(LIKE)</small></Label>
                            <Input
                                id="ulv-action-name"
                                name="actionName"
                                type="text"
                                value={filters.actionName}
                                onChange={onChange('actionName')}
                                placeholder="LIKE match (overrides category)"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label for="ulv-location-id">LocationId</Label>
                            <Input
                                id="ulv-location-id"
                                name="locationId"
                                type="number"
                                value={filters.locationId}
                                onChange={onChange('locationId')}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={2}>
                        <FormGroup>
                            <Label for="ulv-user-id">UserID</Label>
                            <Input
                                id="ulv-user-id"
                                name="userId"
                                type="text"
                                value={filters.userId}
                                onChange={onChange('userId')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label for="ulv-from">From <small className="text-muted">(UTC)</small></Label>
                            <Input
                                id="ulv-from"
                                name="from"
                                type="datetime-local"
                                value={filters.from}
                                onChange={onChange('from')}
                            />
                            <small className="text-muted">
                                Times are interpreted as UTC (the picker shows your local timezone — enter UTC values).
                            </small>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label for="ulv-to">To <small className="text-muted">(UTC)</small></Label>
                            <Input
                                id="ulv-to"
                                name="to"
                                type="datetime-local"
                                value={filters.to}
                                onChange={onChange('to')}
                            />
                            <small className="text-muted">
                                Current UTC: <code>{new Date().toISOString().slice(0, 16).replace('T', ' ')}</code>
                            </small>
                        </FormGroup>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                        <FormGroup className="mb-3">
                            <Button color="primary" type="submit" disabled={loading} data-testid="userlog-search">
                                {loading ? 'Searching...' : 'Search'}
                            </Button>{' '}
                            <Button color="secondary" type="button" onClick={onReset} disabled={loading}>
                                Reset
                            </Button>
                        </FormGroup>
                    </Col>
                </Row>
            </Form>

            {error && (
                <Alert color="danger" data-testid="userlog-error">
                    {error}
                </Alert>
            )}

            <div className="d-flex justify-content-between align-items-center my-2">
                <span className="text-muted" data-testid="userlog-page-info">
                    {rows.length === 0
                        ? (loading ? 'Loading...' : 'No rows. Submit the form to query.')
                        : 'Showing ' + pageStart + ' – ' + pageEnd}
                </span>
                <div>
                    <Button size="sm" outline onClick={onPrev} disabled={!hasPrev || loading} data-testid="userlog-prev">
                        Prev
                    </Button>{' '}
                    <Button size="sm" outline onClick={onNext} disabled={!hasNext || loading} data-testid="userlog-next">
                        Next
                    </Button>
                </div>
            </div>

            {/*
              Single render path — same Table for empty and populated states so
              header layout never changes between submissions. Horizontal scroll
              wrapper handles wide rows (long correlationIds + exception text)
              without crushing the rest of the page.

              Column layout (Phase 999.6 Plan 03):
               COLUMNS (Time/Severity/CorrelationId/Action/InputType/Site/Shot/LocationId/UserID/ExceptionSummary)
               + InputData summary (synthetic)
               + Download (synthetic — file-* rows only)
            */}
            <div style={{ overflowX: 'auto' }} data-testid="userlog-grid">
                <Table size="sm" bordered hover responsive={false}>
                    <thead>
                        <tr>
                            {COLUMNS.map((c) => (
                                <th key={c.key} style={{ whiteSpace: 'nowrap' }}>{c.name}</th>
                            ))}
                            <th style={{ whiteSpace: 'nowrap' }}>InputData</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Download</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={TOTAL_COLS} className="text-center text-muted">
                                    {loading ? 'Loading...' : 'No rows to display.'}
                                </td>
                            </tr>
                        ) : (
                            rows.map((row, i) => {
                                const meta = row ? ACTION_MAP[row.actionName] : undefined;

                                // Row colour: use category color when defined; fall back to
                                // severity-based class for forensic null-color tokens.
                                const rowClass = meta
                                    ? (meta.color
                                        ? colorToRowClass(meta.color)
                                        : severityToRowClass(row.severity))
                                    : severityToRowClass(row && row.severity);

                                const isFile = row && row.inputType &&
                                    typeof row.inputType === 'string' &&
                                    row.inputType.startsWith('file');

                                const isExpanded = row && expandedRow === row.id;
                                const summary = row
                                    ? summariseInputData(row.actionName, row.inputData)
                                    : '';

                                return (
                                    <tr key={(row && row.id != null) ? row.id : i} className={rowClass || undefined}>
                                        {COLUMNS.map((c) => {
                                            const v = row ? row[c.key] : undefined;
                                            // Action column: render human-readable label if known.
                                            if (c.key === 'actionName') {
                                                const label = meta ? meta.label : null;
                                                return (
                                                    <td key={c.key} style={{ whiteSpace: 'nowrap' }}>
                                                        {label != null ? label : (v === null || v === undefined ? '' : String(v))}
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={c.key} style={{ whiteSpace: 'nowrap' }}>
                                                    {v === null || v === undefined ? '' : String(v)}
                                                </td>
                                            );
                                        })}

                                        {/* InputData summary + expand control */}
                                        <td style={{ maxWidth: '300px' }}>
                                            {summary && (
                                                <span style={{ marginRight: '0.4em' }}>{summary}</span>
                                            )}
                                            {row && row.inputData && (
                                                <Button
                                                    size="sm"
                                                    outline
                                                    color="secondary"
                                                    onClick={() => toggleExpand(row.id)}
                                                    style={{ padding: '0 0.3em', fontSize: '0.75em' }}
                                                >
                                                    {isExpanded ? 'Hide' : 'Show'}
                                                </Button>
                                            )}
                                            {isExpanded && (
                                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75em', marginTop: '0.25em' }}>
                                                    {(() => {
                                                        try {
                                                            return JSON.stringify(JSON.parse(row.inputData), null, 2);
                                                        } catch (_) {
                                                            return row.inputData;
                                                        }
                                                    })()}
                                                </pre>
                                            )}
                                        </td>

                                        {/* Download — only on file-* rows */}
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {isFile && (
                                                <Button
                                                    size="sm"
                                                    color="primary"
                                                    outline
                                                    data-testid={'userlog-download-' + row.id}
                                                    disabled={downloading === row.id}
                                                    onClick={() => downloadUserLogFile(row.id)}
                                                >
                                                    {downloading === row.id ? 'Downloading...' : 'Download'}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>
            </div>
        </Container>
    );
}

export default UserLogViewer;
